import type {
    Activity,
    ActivityEntryFormGroup,
    ActivityEntrySubGroup,
    KPI,
} from "@features/report/activity-entry/types/activityEntry";
import type { ReportTemplatePreview } from "@features/report/template/types/reportTemplatePreview";

function mapPreviewKpi(kpi: NonNullable<ReportTemplatePreview["activities"]>[number]["kpis"][number]): KPI {
    return {
        kpi_id: kpi.kpi_id,
        kpi_name: kpi.kpi_name,
        kpi_is_active: kpi.kpi_is_active,
        kpi_list_order: kpi.kpi_list_order,
        kpi_is_required: kpi.kpi_is_required,
        kpi_required_at: kpi.kpi_required_at ?? null,
        data_type: kpi.data_type,
        unit: kpi.unit ?? null,
        submission: null,
        entry_mode: kpi.entry_mode ?? null,
        operation: kpi.operation ?? null,
        decimal_places: kpi.decimal_places ?? null,
        rollup_mode: kpi.rollup_mode ?? null,
        min_selections: kpi.min_selections ?? null,
        max_selections: kpi.max_selections ?? null,
        grid_min_rows: kpi.grid_min_rows ?? null,
        grid_max_rows: kpi.grid_max_rows ?? null,
        grid_show_column_totals: kpi.grid_show_column_totals ?? null,
        grid_show_row_totals: kpi.grid_show_row_totals ?? null,
        operands: kpi.operands ?? null,
        options: kpi.options ?? null,
        grid_columns: kpi.grid_columns ?? null,
        grid_fixed_rows: kpi.grid_fixed_rows ?? null,
    };
}

function mapPreviewActivities(
    activities: ReportTemplatePreview["activities"] = []
): Activity[] {
    return (activities ?? []).map((activity) => ({
        activity_id: activity.activity_id,
        activity_name: activity.activity_name,
        activity_is_active: activity.activity_is_active,
        activity_list_order: activity.activity_list_order ?? 0,
        kpis: (activity.kpis ?? []).map(mapPreviewKpi),
    }));
}

/**
 * Maps template preview API payload(s) into activity-entry form groups.
 * Preserves full KPI config (options, grid, formula) when the preview endpoint returns it.
 */
export function mapTemplatePreviewToFormGroups(
    preview: ReportTemplatePreview | ReportTemplatePreview[] | null | undefined
): ActivityEntryFormGroup[] {
    const previewGroups = Array.isArray(preview)
        ? preview
        : preview
            ? [preview]
            : [];

    return previewGroups
        .filter((group) => Boolean(group?.report_template))
        .map((group) => {
            const hasSubGroups =
                Boolean(group.has_subgroups) || (group.sub_groups?.length ?? 0) > 0;

            const subGroups: ActivityEntrySubGroup[] = hasSubGroups
                ? (group.sub_groups ?? []).map((subGroup) => ({
                      report_sub_group_id: subGroup.report_sub_group_id ?? null,
                      sub_group_name: subGroup.sub_group_name ?? null,
                      sub_group_list_order: subGroup.sub_group_list_order ?? 0,
                      activities: mapPreviewActivities(subGroup.activities ?? []),
                  }))
                : [];

            return {
                report_template: {
                    report_template_id: group.report_template.report_template_id,
                    report_template_name: group.report_template.report_template_name,
                    department_id: group.report_template.department_id,
                    department_name: group.report_template.department_name ?? "",
                    origin_scope: group.report_template.origin_scope ?? null,
                    activity_report_id: null,
                    activity_report_status_id: null,
                    activity_report_status_name: null,
                    process_results: null,
                },
                has_subgroups: hasSubGroups,
                sub_groups: subGroups,
                activities: hasSubGroups ? [] : mapPreviewActivities(group.activities ?? []),
            };
        });
}
